/**
 * Access-token lifecycle for the OIDC session. Access tokens are short-lived
 * (15 min) JWTs; the refresh token rotates on every use (single-use), so all
 * refreshes are funneled through ONE in-flight promise and the rotated
 * refresh token is persisted BEFORE the promise resolves — two concurrent
 * 401 retries must never burn the token.
 */

import * as oidc from './oidcService';
import { pushSyncAuth } from './syncAuthBridge';
import { isWebApp } from '../platform/target';
import { adoptRefreshToken, forgetWebSession, hasWebSession, refreshWebSession } from './webSession';

const ACCESS_KEY = 'authToken'; // unchanged key: attachAuthHeaders/socket/sync keep working
const REFRESH_KEY = 'authRefreshToken';
const EXPIRES_KEY = 'authTokenExpiresAt';

// Refresh proactively when less than this remains.
const SKEW_MS = 60_000;

let inFlight = null;

export function getAccessToken() {
  return localStorage.getItem(ACCESS_KEY);
}

export function getRefreshToken() {
  return localStorage.getItem(REFRESH_KEY);
}

export function getExpiresAt() {
  const raw = localStorage.getItem(EXPIRES_KEY);
  return raw ? Number(raw) : null;
}

export function hasRefreshableSession() {
  // Web app (card 2.3): the refresh token is an HttpOnly cookie JavaScript
  // cannot see; a non-secret flag says a cookie session exists.
  if (isWebApp()) return hasWebSession() || Boolean(getRefreshToken());
  return Boolean(getRefreshToken());
}

export function persistTokens({ accessToken, refreshToken, expiresAt }) {
  localStorage.setItem(ACCESS_KEY, accessToken);
  // Never store a refresh token in the browser's storage on the web app.
  if (isWebApp()) localStorage.removeItem(REFRESH_KEY);
  else if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
  if (expiresAt) localStorage.setItem(EXPIRES_KEY, String(expiresAt));
  else localStorage.removeItem(EXPIRES_KEY);
  scheduleProactiveRefresh();
  // Rust sync engine uses the same bearer — keep it current (best-effort).
  try {
    pushSyncAuth(accessToken);
  } catch {
    /* sync engine unavailable */
  }
}

export function clearTokens() {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(EXPIRES_KEY);
  // Web app: without this the marker outlives the sign-out and the next
  // request refreshes the user straight back in off the cookie.
  if (isWebApp()) forgetWebSession();
  if (proactiveTimer) clearTimeout(proactiveTimer);
  proactiveTimer = null;
}

// The HTTP interceptors refresh lazily, which only keeps the stored token
// fresh while the app is making API calls. But the Phoenix socket presents
// the stored token on every reconnect handshake, and the server rejects an
// expired one (so the reconnect loop can pick up a fresh token) — an idle
// app (15+ min without HTTP traffic) whose network blips would otherwise
// stay disconnected until its next API call. Refreshing shortly before
// expiry keeps the stored token always presentable. Re-armed on every
// persist; a failed attempt retries on a short backoff (network may be the
// very thing that is down).
const RETRY_MS = 60_000;
let proactiveTimer = null;

function scheduleProactiveRefresh(delayOverride) {
  if (typeof setTimeout !== 'function') return;
  if (proactiveTimer) clearTimeout(proactiveTimer);
  proactiveTimer = null;
  if (!hasRefreshableSession()) return;
  const expiresAt = getExpiresAt();
  if (!expiresAt) return;
  const delay = delayOverride ?? Math.max(expiresAt - SKEW_MS - Date.now(), 10_000);
  proactiveTimer = setTimeout(() => {
    proactiveTimer = null;
    refreshNow().catch(() => {
      if (hasRefreshableSession()) scheduleProactiveRefresh(RETRY_MS);
    });
    // Success needs no explicit reschedule: doRefresh → persistTokens re-arms.
  }, delay);
}

/** Arm the proactive refresh for an already-persisted session (app boot). */
export function startProactiveRefresh() {
  scheduleProactiveRefresh();
}

/**
 * Returns a currently-valid access token, refreshing first when it is about
 * to expire. Resolves to the token, or null when there is no session /
 * nothing to refresh with (legacy tokens without a refresh token pass
 * through untouched — the 401 path handles them).
 */
export async function ensureFreshToken() {
  const access = getAccessToken();
  if (!access) return null;

  const expiresAt = getExpiresAt();
  const needsRefresh = expiresAt && Date.now() >= expiresAt - SKEW_MS;

  if (!needsRefresh || !hasRefreshableSession()) return access;

  try {
    return await refreshNow();
  } catch {
    // Let the request proceed with the stale token — the 401 handler decides.
    return getAccessToken();
  }
}

/**
 * A refresh that has not settled after this long is given up (05.10.2026): a
 * token request that never answered (sleep, network change, a proxy that holds
 * the connection) kept `inFlight` pending forever, every later refresh joined
 * it, and the session never recovered. Rust also times the request out now.
 */
export const REFRESH_TIMEOUT_MS = 45_000;

/** Force a refresh (single-flight). Rejects when the session is gone. */
export function refreshNow() {
  if (!inFlight) {
    let timer;
    const timeout = new Promise((_, reject) => {
      timer = setTimeout(() => reject(new Error('refresh_timeout')), REFRESH_TIMEOUT_MS);
    });
    inFlight = Promise.race([doRefresh(), timeout]).finally(() => {
      clearTimeout(timer);
      inFlight = null;
    });
  }
  return inFlight;
}

async function doRefresh() {
  if (isWebApp()) {
    // A refresh token left in storage by an earlier web build is moved into
    // the cookie once, then removed.
    // A cookie session always wins: sending a stale token from an older build
    // would make the server refuse it and clear a perfectly good cookie.
    const legacy = hasWebSession() ? null : getRefreshToken();
    try {
      const tokens = legacy ? await adoptRefreshToken(legacy) : await refreshWebSession();
      persistTokens(tokens);
      return tokens.accessToken;
    } finally {
      // Spent or refused, a single-use legacy token is never worth a retry.
      if (legacy) localStorage.removeItem(REFRESH_KEY);
    }
  }
  const refreshToken = getRefreshToken();
  if (!refreshToken) throw new Error('NO_REFRESH_TOKEN');

  try {
    const tokens = await oidc.refresh(refreshToken);
    persistTokens(tokens); // rotated refresh token saved before resolving waiters
    return tokens.accessToken;
  } catch (error) {
    // Rotation race guard: another window may have rotated the token while we
    // held the old one. Re-read once — if it changed, that refresh succeeded.
    const latest = getRefreshToken();
    if (latest && latest !== refreshToken) {
      return getAccessToken();
    }
    throw error;
  }
}
